import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Star,
  Heart,
  Briefcase,
  Lightbulb,
  Calendar,
  User,
  Target,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

export default function ResultPage() {
  const navigate = useNavigate();
  const [resultText, setResultText] = useState("");
  const [loading, setLoading] = useState(true);
  const [parsedData, setParsedData] = useState({
    name: "",
    birthdate: "",
    numbers: {},
    sections: {},
  });

  useEffect(() => {
    // Ambil hasil dari localStorage
    const stored = localStorage.getItem("predictionResult");

    if (!stored) {
      toast.error("Hasil ramalan tidak ditemukan. Silakan ulangi.");
      navigate("/input");
      return;
    }

    setResultText(stored);
    parseMarkdown(stored);
    setLoading(false);
  }, [navigate]);

  const parseMarkdown = (text) => {
    const lines = text.split("\n");
    const data = {
      name: "",
      birthdate: "",
      numbers: {},
      sections: {},
    };

    let currentSection = "";
    let sectionContent = [];

    lines.forEach((line, index) => {
      // Parse nama - support both formats
      if (
        line.includes("**Nama Lengkap:**") ||
        line.includes("**Nama Lengkap**:")
      ) {
        const parts = line.split(":");
        if (parts[1]) data.name = parts[1].replace(/\*\*/g, "").trim();
      }

      // Parse tanggal lahir - support both formats
      if (
        line.includes("**Tanggal Lahir:**") ||
        line.includes("**Tanggal Lahir**:")
      ) {
        const parts = line.split(":");
        if (parts[1]) data.birthdate = parts[1].replace(/\*\*/g, "").trim();
      }

      // Parse numbers dengan format yang lebih fleksibel
      if (line.includes("Life Path Number:")) {
        const match = line.match(/\*\*(\d+)\*\*/);
        if (match) data.numbers.lifePath = match[1];
      }
      if (line.includes("Expression/Destiny Number:")) {
        const match = line.match(/\*\*(\d+)\*\*/);
        if (match) data.numbers.expression = match[1];
      }
      if (line.includes("Soul Urge Number:")) {
        const match = line.match(/\*\*(\d+)\*\*/);
        if (match) data.numbers.soulUrge = match[1];
      }
      if (line.includes("Personality Number:")) {
        const match = line.match(/\*\*(\d+)\*\*/);
        if (match) data.numbers.personality = match[1];
      }
      if (line.includes("Birthday Number:")) {
        const match = line.match(/\*\*(\d+)\*\*/);
        if (match) data.numbers.birthday = match[1];
      }
      if (line.includes("Maturity Number:")) {
        const match = line.match(/\*\*(\d+)\*\*/);
        if (match) data.numbers.maturity = match[1];
      }

      // Capture section content dengan header yang berbeda
      if (line.startsWith("## Kepribadian Lengkap")) {
        if (currentSection && sectionContent.length > 0) {
          data.sections[currentSection] = sectionContent;
        }
        currentSection = "personality";
        sectionContent = [];
      } else if (
        line.startsWith("## Cinta & Hubungan") ||
        line.startsWith("## Cinta")
      ) {
        if (currentSection && sectionContent.length > 0) {
          data.sections[currentSection] = sectionContent;
        }
        currentSection = "love";
        sectionContent = [];
      } else if (
        line.startsWith("## Karier & Potensi Finansial") ||
        line.startsWith("## Karier")
      ) {
        if (currentSection && sectionContent.length > 0) {
          data.sections[currentSection] = sectionContent;
        }
        currentSection = "career";
        sectionContent = [];
      } else if (
        line.startsWith("## Insight dan Saran Hidup") ||
        line.startsWith("## Insight")
      ) {
        if (currentSection && sectionContent.length > 0) {
          data.sections[currentSection] = sectionContent;
        }
        currentSection = "insights";
        sectionContent = [];
      } else if (line.startsWith("## Strength & Weakness")) {
        if (currentSection && sectionContent.length > 0) {
          data.sections[currentSection] = sectionContent;
        }
        currentSection = "strengthWeakness";
        sectionContent = [];
      } else if (line.startsWith("## Peta Ramalan Hidup 2026")) {
        if (currentSection && sectionContent.length > 0) {
          data.sections[currentSection] = sectionContent;
        }
        currentSection = "ramalan2026";
        sectionContent = [];
      } else if (
        currentSection &&
        !line.startsWith("##") &&
        !line.startsWith("---") &&
        line.trim()
      ) {
        // Clean up markdown formatting
        const cleanLine = line.replace(/\*\*/g, "").replace(/- /g, "").trim();
        if (cleanLine) sectionContent.push(cleanLine);
      }
    });

    // Save last section
    if (currentSection && sectionContent.length > 0) {
      data.sections[currentSection] = sectionContent;
    }

    setParsedData(data);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 text-center border border-white/20">
          <div className="animate-spin inline-block mb-4">
            <Sparkles className="w-16 h-16 text-yellow-300" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-white">
            Menyelaraskan Energi Kosmik...
          </h2>
          <p className="text-purple-200">Mohon tunggu sebentar</p>
        </div>
      </div>
    );
  }

  const NumberCard = ({ icon: Icon, label, number, color }) => (
    <div
      className={`bg-gradient-to-br ${color} rounded-2xl p-6 text-white shadow-lg hover:scale-105 transition-transform`}
    >
      <Icon className="w-8 h-8 mb-3 opacity-80" />
      <div className="text-5xl font-bold mb-2">{number}</div>
      <div className="text-sm opacity-90 font-medium">{label}</div>
    </div>
  );

  const SectionCard = ({ icon: Icon, title, content, gradient }) => (
    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 transition-colors">
      <div className="flex items-start gap-4 mb-4">
        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white flex-1">{title}</h3>
      </div>
      <div className="text-purple-100 leading-relaxed space-y-2">
        {content?.map((line, i) => (
          <p key={i} className="text-sm sm:text-base">
            {line}
          </p>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-500 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-6 sm:py-12 max-w-7xl">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm sm:text-base">Kembali</span>
          </button>
          <button
            onClick={() => navigate("/input")}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 transition-colors text-sm sm:text-base"
          >
            Ramalan Baru
          </button>
        </div>

        {/* Welcome Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 sm:p-12 mb-6 sm:mb-8 text-center border border-white/20">
          <div className="inline-flex items-center justify-center mb-4">
            <Star className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-300 animate-pulse" />
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3 text-white">
            Hasil Ramalan{" "}
            <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
              Anda
            </span>
          </h1>
          <p className="text-purple-200 text-sm sm:text-base">
            Berikut interpretasi lengkap dari energi kosmik Anda
          </p>

          {/* Personal Info */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-yellow-300" />
              <span className="font-semibold text-sm sm:text-base">
                {parsedData.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-yellow-300" />
              <span className="text-sm sm:text-base">
                {parsedData.birthdate}
              </span>
            </div>
          </div>
        </div>

        {/* Core Numbers Grid */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-yellow-300" />
            Angka Inti Anda
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            <NumberCard
              icon={Target}
              label="Life Path"
              number={parsedData.numbers.lifePath}
              color="from-purple-500 to-pink-500"
            />
            <NumberCard
              icon={Star}
              label="Expression"
              number={parsedData.numbers.expression}
              color="from-blue-500 to-cyan-500"
            />
            <NumberCard
              icon={Heart}
              label="Soul Urge"
              number={parsedData.numbers.soulUrge}
              color="from-pink-500 to-rose-500"
            />
            <NumberCard
              icon={User}
              label="Personality"
              number={parsedData.numbers.personality}
              color="from-indigo-500 to-purple-500"
            />
            <NumberCard
              icon={Calendar}
              label="Birthday"
              number={parsedData.numbers.birthday}
              color="from-green-500 to-emerald-500"
            />
            <NumberCard
              icon={TrendingUp}
              label="Maturity"
              number={parsedData.numbers.maturity}
              color="from-orange-500 to-red-500"
            />
          </div>
        </div>

        {/* Detailed Sections */}
        <div className="space-y-4 sm:space-y-6">
          <SectionCard
            icon={User}
            title="Kepribadian"
            content={parsedData.sections.personality}
            gradient="from-purple-500 to-pink-500"
          />

          <SectionCard
            icon={Heart}
            title="Cinta & Hubungan"
            content={parsedData.sections.love}
            gradient="from-pink-500 to-rose-500"
          />

          <SectionCard
            icon={Briefcase}
            title="Karier & Potensi Finansial"
            content={parsedData.sections.career}
            gradient="from-blue-500 to-cyan-500"
          />
          <SectionCard
            icon={Star}
            title="Strength & Weakness"
            content={parsedData.sections.strengthWeakness}
            gradient="from-green-500 to-teal-500"
          />


          <SectionCard
            icon={Lightbulb}
            title="Insight dan Saran Hidup"
            content={parsedData.sections.insights}
            gradient="from-yellow-500 to-orange-500"
          />
          
          <SectionCard
            icon={Calendar}
            title="Peta Ramalan Hidup 2026"
            content={parsedData.sections.ramalan2026}
            gradient="from-red-500 to-orange-500"
          />
        </div>

        {/* Full Markdown (Hidden on mobile, shown on desktop) */}
        {/* <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hidden lg:block">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>Analisis Lengkap</span>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
              Detail
            </span>
          </h3>

          <div
            className="
      prose prose-invert max-w-none 
      prose-headings:font-bold 
      prose-headings:text-white 
      prose-h1:text-3xl 
      prose-h2:text-2xl 
      prose-h3:text-xl 
      prose-p:leading-relaxed 
      prose-p:text-gray-200
      prose-strong:text-white
      prose-li:marker:text-white/60
      prose-ul:space-y-2
      prose-li:text-gray-200
      prose-blockquote:border-l-white/20 
      prose-blockquote:text-gray-300
      prose-blockquote:italic
      prose-code:bg-black/40
      prose-code:px-1.5
      prose-code:py-0.5
      prose-code:rounded-md
      prose-pre:bg-black/40
      prose-pre:p-4
      prose-pre:rounded-xl
    "
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {resultText}
            </ReactMarkdown>
          </div>
        </div> */}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }

        /* Markdown styles */
        .prose h1, .prose h2, .prose h3 {
          color: white;
        }
        .prose p, .prose li {
          color: rgba(255, 255, 255, 0.8);
        }
        .prose strong {
          color: #fbbf24;
        }
        .prose table {
          color: white;
        }
        .prose th {
          background: rgba(255, 255, 255, 0.1);
        }
        .prose td {
          border-color: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
}
